
import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from "@angular/core";
import { Linqset, LinqsetNode, NODE_SUPERTYPE, getConnectedModels } from "../../common/linqset-model";
import { LinqsetRepositoryService } from "../../model-services/linqset-repository.service";
import { getTagGroupsForModel, Organisation, TagGroup } from "../../common/organisation";
import { TagsService } from "../../organisation/tags/tags-service";
import { USER_ROLE } from "../../common/user";
import { Subscription } from "rxjs";
import { OrganisationService } from "../../organisation/organisation.service";

interface ExternalNodeGroup {
    name: string
    _key: string
    nodes: LinqsetNode[];
}

@Component({
    selector: 'app-palette-bar',
    templateUrl: 'palette-bar.html',
    styleUrls: ['palette-bar.scss'],
    standalone: false
})
export class PaletteBarComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() linqset:Linqset
    @Input() organisation:Organisation
    @Input() selectedTagGroup:TagGroup
    @Output() addExternalNodeEvent = new EventEmitter<LinqsetNode>()
    @Output() tagGroupEvent = new EventEmitter<TagGroup>();

    updatedOrganisationSub:Subscription
    groups:ExternalNodeGroup[] = []

    tagGroups:TagGroup[]

    smallScreen:boolean
    isOrganisationModel:boolean
    
    constructor(private repoService:LinqsetRepositoryService, private tagsService:TagsService, private organisationService:OrganisationService) {}

    ngOnDestroy(): void {
        if (this.updatedOrganisationSub) this.updatedOrganisationSub.unsubscribe()
    }

    ngAfterViewInit(): void {
        this.updatedOrganisationSub = this.organisationService.updatedOrganisationEvent.subscribe(e => {
            this.loadData()
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['linqset'] && this.linqset && this.organisation) this.loadData()
    }

    loadData() {
        if (!this.linqset || !this.organisation) return
        this.loadExternalNodes()
        this.tagGroups = getTagGroupsForModel(this.organisation, this.linqset.modelKey)
    }

    loadExternalNodes() {
        this.isOrganisationModel = this.linqset.isOrganisationModel()
        if (this.isOrganisationModel) return

        const externalInputNodes = this.repoService.organisationLinqset?this.repoService.organisationLinqset.nodes.filter(n => n.external && n.superType === NODE_SUPERTYPE.INFORMATION && n.modelKey !== this.linqset.modelKey):[]

        if (!this.organisation || !this.repoService.organisationLinqset) return
        const excludedModelKeys = getConnectedModels(this.linqset.model, this.organisation.models, this.repoService.organisationLinqset).map(m => m._key)
            .concat(this.organisation.models.filter(m => m.deletedDate).map(m => m._key))
        this.groups = externalInputNodes?externalInputNodes.filter(n => excludedModelKeys.indexOf(n.modelKey) < 0 && !this.linqset.nodes.find(ln => ln._key === n._key)).reduce((groups:ExternalNodeGroup[],n:LinqsetNode) => {
            let group = groups.find(g => g._key === n.modelKey)
            if (!group) {
                const model = this.organisation.models.find(s => s._key === n.modelKey)
                group = {
                    _key:n.modelKey,
                    name: model && model.name,
                    nodes: []
                } as ExternalNodeGroup
                groups.push(group)
            }
            group.nodes.push(n)
            return groups
        }, []):[]
        this.groups = this.groups.sort((a,b) => a.name?.toLowerCase() > b.name?.toLowerCase()?1:-1)
    }

    externalNodeSelected(node:LinqsetNode) {
        this.addExternalNodeEvent.emit(node)
    }

    // a new tag group is shown straight away, if it applies to this model
    async createTagGroup() {
        const before = new Set((this.organisation.tagGroups || []).map(tg => tg._key))
        if (await this.tagsService.openTagGroupDialog() !== 'saved') return
        this.tagGroups = getTagGroupsForModel(this.organisation, this.linqset.modelKey)
        const created = this.tagGroups.find(tg => !before.has(tg._key))
        if (created) this.selectTagGroup(created)
    }

    hasWriteAccess() {
        return this.organisation?.role && this.organisation.role !== USER_ROLE.READ
    }
    
    // undefined hides tags
    selectTagGroup(tg:TagGroup) {
        this.tagGroupEvent.emit(tg)
    }

    onResize(ev) {
        if (ev.contentRect.width < 1300) {
            if (!this.smallScreen) this.smallScreen = true
        } else {
            if (this.smallScreen) this.smallScreen = false
        }
    }

}